import { toast } from "sonner";
import * as z from "zod/v3";

import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { Textarea } from "@workspace/ui/components/textarea";

import { useCreateLedgerAccount } from "../api";
import type { LedgerAccountType } from "../types";

const accountTypes: LedgerAccountType[] = [
	"Asset",
	"Liability",
	"Equity",
	"Income",
	"Expense",
	"Payroll",
];

const currencies = ["USD", "EUR", "GBP", "HNL", "MXN", "CAD", "AUD"];

const createAccountSchema = z.object({
	name: z.string().min(1, "Name is required").max(100),
	code: z
		.string()
		.min(1, "Code is required")
		.max(20)
		.regex(
			/^[A-Z0-9-]+$/,
			"Code must be uppercase letters, numbers, or dashes",
		),
	accountType: z.enum([
		"Asset",
		"Liability",
		"Equity",
		"Income",
		"Expense",
		"Payroll",
	]),
	baseCurrency: z.string().min(3).max(3),
	description: z.string().max(500).optional(),
});

type CreateAccountFormValues = z.infer<typeof createAccountSchema>;

interface CreateAccountFormProps {
	tenant: string;
	onSuccess?: () => void;
	parentAccountId?: string;
}

export function CreateAccountForm({
	tenant,
	onSuccess,
	parentAccountId,
}: CreateAccountFormProps) {
	const createAccount = useCreateLedgerAccount(tenant);

	const form = useAppForm({
		defaultValues: {
			name: "",
			code: "",
			accountType: "Asset",
			baseCurrency: "USD",
			description: "",
		} as CreateAccountFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: createAccountSchema,
		},
		onSubmit: async ({ value }) => {
			try {
				await createAccount.mutateAsync({
					...value,
					parentAccountId,
				});
				toast.success("Account created successfully");
				form.reset();
				onSuccess?.();
			} catch (error) {
				toast.error(
					error instanceof Error ? error.message : "Failed to create account",
				);
			}
		},
	});

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
			className="space-y-4"
		>
			<form.AppField name="code">
				{(field) => (
					<field.FormItem>
						<field.FormLabel>Account Code</field.FormLabel>
						<field.FormControl>
							<Input
								name={field.name}
								placeholder="CASH-001"
								className="font-mono uppercase"
								value={field.state.value || ""}
								onChange={(e) =>
									field.handleChange(e.target.value.toUpperCase())
								}
							/>
						</field.FormControl>
						<field.FormDescription>
							Unique identifier for this account (e.g., CASH-001, AR-100)
						</field.FormDescription>
						<field.FormMessage />
					</field.FormItem>
				)}
			</form.AppField>

			<form.AppField name="name">
				{(field) => (
					<field.FormItem>
						<field.FormLabel>Account Name</field.FormLabel>
						<field.FormControl>
							<Input
								name={field.name}
								placeholder="Cash on Hand"
								value={field.state.value || ""}
								onChange={(e) => field.handleChange(e.target.value)}
							/>
						</field.FormControl>
						<field.FormMessage />
					</field.FormItem>
				)}
			</form.AppField>

			<div className="grid grid-cols-2 gap-4">
				<form.AppField name="accountType">
					{(field) => (
						<field.FormItem>
							<field.FormLabel>Account Type</field.FormLabel>
							<Select
								value={field.state.value}
								onValueChange={(v) =>
									field.handleChange(v as LedgerAccountType)
								}
							>
								<field.FormControl>
									<SelectTrigger>
										<SelectValue placeholder="Select type" />
									</SelectTrigger>
								</field.FormControl>
								<SelectContent>
									{accountTypes.map((type) => (
										<SelectItem key={type} value={type}>
											{type}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<field.FormMessage />
						</field.FormItem>
					)}
				</form.AppField>

				<form.AppField name="baseCurrency">
					{(field) => (
						<field.FormItem>
							<field.FormLabel>Base Currency</field.FormLabel>
							<Select
								value={field.state.value || "USD"}
								onValueChange={field.handleChange}
							>
								<field.FormControl>
									<SelectTrigger>
										<SelectValue placeholder="Select currency" />
									</SelectTrigger>
								</field.FormControl>
								<SelectContent>
									{currencies.map((currency) => (
										<SelectItem key={currency} value={currency}>
											{currency}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<field.FormMessage />
						</field.FormItem>
					)}
				</form.AppField>
			</div>

			<form.AppField name="description">
				{(field) => (
					<field.FormItem>
						<field.FormLabel>Description (Optional)</field.FormLabel>
						<field.FormControl>
							<Textarea
								name={field.name}
								placeholder="Brief description of this account's purpose"
								rows={2}
								value={field.state.value || ""}
								onChange={(e) => field.handleChange(e.target.value)}
							/>
						</field.FormControl>
						<field.FormMessage />
					</field.FormItem>
				)}
			</form.AppField>

			<div className="flex justify-end gap-2 pt-4">
				<form.Subscribe selector={(state) => state.isSubmitting}>
					{(isSubmitting) => (
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? "Creating..." : "Create Account"}
						</Button>
					)}
				</form.Subscribe>
			</div>
		</form>
	);
}
